/**
 * MOBILE HERO — SECTION 1 · top nav bar (design map §2-1 · prototype lines
 * 31–46).
 *
 * 58px bar, shared {@link MOBILE_HERO_GUTTER} side padding. Logo left,
 * Preise + Docs + Karriere centered between logo and theme toggle, Anmelden top-right as white button.
 */
import { handbookHref } from "@/lib/help-center-seo";
import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { WebsiteThemeToggle } from "../../website-theme";
import { Logo } from "./hero-primitives";
import { H, MOBILE_HERO_GUTTER } from "./hero-tokens";
import { OPEN_POSITIONS_COUNT } from "./open-positions-count";

const TEXT_LINK: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: H.ink,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const LOGIN_BTN: CSSProperties = {
  marginLeft: 2,
  background: ld("#fff", "#181b1f"),
  color: H.ink,
  border: `1px solid ${ld("rgba(11,15,25,0.12)", "rgba(250,250,250,0.12)")}`,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 12px",
  borderRadius: 9,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const BADGE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 999,
  background: H.accent,
  color: ld("#fff", "#181b1f"),
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
};

export function HeroMobileNav(): ReactNode {
  const t = useTranslations("marketing.heroMobileNav");
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        margin: "0 auto",
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: H.surface,
        borderBottom: `1px solid ${ld("rgba(11,15,25,0.08)", "rgba(250,250,250,0.08)")}`,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          height: 200,
          background: H.surface,
        }}
      />
      <div
        style={{
          position: "relative",
          height: 58,
          display: "flex",
          alignItems: "center",
          padding: `0 ${MOBILE_HERO_GUTTER}px`,
        }}
      >
        <Link
          href="/"
          aria-label={BRAND_HOME_ARIA_LABEL}
          title={BRAND_LOGO_TITLE}
          style={{ display: "inline-flex", textDecoration: "none" }}
        >
          <Logo size={24} iconOnly />
        </Link>

        {/* Centered in the room between logo and the right group: never runs into the theme toggle. */}
        <nav
          aria-label={t("hauptnavigation")}
          style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <Link href="/preise" style={TEXT_LINK}>
              {t("preise")}
            </Link>
            <a href={handbookHref()} style={TEXT_LINK}>
              {t("handbuch")}
            </a>
            <Link
              href="/careers"
              aria-label={t("karriereOffeneStellen", {
                count: OPEN_POSITIONS_COUNT,
              })}
              style={{
                ...TEXT_LINK,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {t("karriere")}
              <span style={BADGE} aria-hidden="true">
                {OPEN_POSITIONS_COUNT}
              </span>
            </Link>
          </div>
        </nav>

        <WebsiteThemeToggle />
        <Link href="/login" className="mkhero-nav-loginbtn" style={LOGIN_BTN}>
          {t("anmelden")}
        </Link>
      </div>
    </header>
  );
}
